import React, { createContext, useState, useContext, useEffect } from "react";
import config from "../config";

// Create the context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isCheckingAuth: true,
    user: null,
    isAdmin: false,
    isCheckingAdmin: true,
  });

  // Check authentication and admin status on initial load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if user is authenticated
        const authResponse = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.auth.checkAuth}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const authData = await authResponse.json();

        if (authResponse.ok && authData.authenticated) {
          // User is authenticated, now check admin status
          console.log(authData.user);
          try {
            const adminResponse = await fetch(
              `${config.api.baseUrl}${config.api.endpoints.admin.checkAdmin}`,
              {
                method: "GET",
                credentials: "include",
              }
            );

            const adminData = await adminResponse.json();

            setAuthState({
              isAuthenticated: true,
              isCheckingAuth: false,
              user: authData.user,
              isAdmin:
                adminResponse.ok && adminData.success && adminData.is_admin,
              isCheckingAdmin: false,
            });
          } catch (error) {
            console.error("Admin check error:", error);
            setAuthState({
              isAuthenticated: true,
              isCheckingAuth: false,
              user: authData.user,
              isAdmin: false,
              isCheckingAdmin: false,
            });
          }
        } else {
          // User is not authenticated
          setAuthState({
            isAuthenticated: false,
            isCheckingAuth: false,
            user: null,
            isAdmin: false,
            isCheckingAdmin: false,
          });
        }
      } catch (error) {
        console.error("Authentication check error:", error);
        setAuthState({
          isAuthenticated: false,
          isCheckingAuth: false,
          user: null,
          isAdmin: false,
          isCheckingAdmin: false,
        });
      }
    };

    checkAuth();
  }, []);

  // Login function to update auth state when user logs in
  const login = async (userData) => {
    setAuthState((prev) => ({
      ...prev,
      isAuthenticated: true,
      user: userData,
      isCheckingAuth: false,
    }));

    // Check admin status after login
    try {
      const adminResponse = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.checkAdmin}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      const adminData = await adminResponse.json();

      setAuthState((prev) => ({
        ...prev,
        isAdmin: adminResponse.ok && adminData.success && adminData.is_admin,
        isCheckingAdmin: false,
      }));
    } catch (error) {
      console.error("Admin check error:", error);
      setAuthState((prev) => ({
        ...prev,
        isAdmin: false,
        isCheckingAdmin: false,
      }));
    }
  };

  // Logout function
  const logout = () => {
    setAuthState({
      isAuthenticated: false,
      isCheckingAuth: false,
      user: null,
      isAdmin: false,
      isCheckingAdmin: false,
    });
  };

  // Value to be provided by the context
  const contextValue = {
    ...authState,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
